import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BedDouble, Building, Users, CheckCircle, AlertTriangle, Wrench, Loader2, Plus, MapPin } from 'lucide-react';
import api from '@/lib/api';
import GoogleMapPicker from '@/components/GoogleMapPicker';

export const HousingManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [newUnit, setNewUnit] = useState({ 
    name: '', 
    building: '', 
    capacity: 1, 
    notes: '',
    address: '',
    latitude: null,
    longitude: null
  });
  const [filter, setFilter] = useState('all');

  const { data: units, isLoading } = useQuery({
    queryKey: ['housing', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/housing/units`).then(r => r.data),
  });

  const createUnitMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/housing/units`, { ...data, event_id: eventId }),
    onSuccess: () => {
      toast.success('Housing unit added!');
      queryClient.invalidateQueries(['housing', eventId]);
      setShowAddUnit(false);
      setNewUnit({ name: '', building: '', capacity: 1, notes: '', address: '', latitude: null, longitude: null });
    },
    onError: () => toast.error('Failed to add unit'),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ unitId, status }) => api.post(`/admin/events/${eventId}/housing/units/${unitId}/assign`, { status }),
    onSuccess: () => {
      toast.success('Status updated!');
      queryClient.invalidateQueries(['housing', eventId]);
    },
  });

  const filteredUnits = units?.filter(unit => {
    if (filter === 'all') return true;
    return unit.status === filter;
  });

  const stats = {
    total: units?.length || 0,
    ready: units?.filter(u => u.status === 'ready').length || 0,
    occupied: units?.filter(u => u.status === 'occupied').length || 0,
    dirty: units?.filter(u => u.status === 'dirty').length || 0,
    maintenance: units?.filter(u => u.status === 'maintenance').length || 0,
  };

  const statusConfig = {
    ready: { color: 'bg-green-500', icon: CheckCircle, label: 'Ready' },
    occupied: { color: 'bg-blue-500', icon: Users, label: 'Occupied' },
    dirty: { color: 'bg-yellow-500', icon: AlertTriangle, label: 'Dirty' },
    maintenance: { color: 'bg-red-500', icon: Wrench, label: 'Maintenance' },
  };

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-slate-200 rounded-lg" />;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-slate-500">Total Units</div>
          </CardContent>
        </Card>
        {Object.entries(statusConfig).map(([status, config]) => (
          <Card key={status} className="cursor-pointer hover:shadow-md" onClick={() => setFilter(status)}>
            <CardContent className="pt-4 text-center">
              <div className="text-2xl font-bold">{stats[status]}</div>
              <div className="text-sm text-slate-500 flex items-center justify-center gap-1">
                <config.icon className="h-3 w-3" /> {config.label}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Units</SelectItem>
            <SelectItem value="ready">Ready</SelectItem>
            <SelectItem value="occupied">Occupied</SelectItem>
            <SelectItem value="dirty">Dirty</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowAddUnit(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Unit
        </Button>
      </div>

      {/* Units Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUnits?.map((unit) => {
          const config = statusConfig[unit.status] || statusConfig.ready;
          return (
            <Card key={unit.id}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BedDouble className="h-5 w-5" />
                      {unit.name}
                    </CardTitle>
                    {unit.building && (
                      <CardDescription className="flex items-center gap-1">
                        <Building className="h-3 w-3" /> {unit.building}
                      </CardDescription>
                    )}
                  </div>
                  <Badge className={config.color}>
                    <config.icon className="h-3 w-3 mr-1" />
                    {config.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Capacity</span>
                    <span className="font-medium">{unit.occupied || 0}/{unit.capacity} guests</span>
                  </div>
                  
                  {/* Occupancy Bar */}
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        unit.is_full ? 'bg-red-500' : 
                        (unit.occupied || 0) > 0 ? 'bg-blue-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(100, ((unit.occupied || 0) / unit.capacity) * 100)}%` }}
                    />
                  </div>
                  
                  {/* Assigned Delegates List */}
                  {unit.assigned_delegates?.length > 0 && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-slate-500 mb-2">Assigned Guests:</p>
                      <div className="space-y-1">
                        {unit.assigned_delegates.map(delegate => (
                          <div key={delegate.id} className="flex items-center justify-between text-sm bg-blue-50 px-2 py-1 rounded">
                            <span className="truncate">{delegate.name}</span>
                            {delegate.checked_in && (
                              <Badge variant="outline" className="text-xs ml-2">Checked In</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {unit.notes && (
                    <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded">{unit.notes}</p>
                  )}

                  {/* Status Actions */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {unit.status !== 'ready' && (
                      <Button size="sm" variant="outline" onClick={() => updateStatusMutation.mutate({ unitId: unit.id, status: 'ready' })}>
                        Mark Ready
                      </Button>
                    )}
                    {unit.status !== 'dirty' && (
                      <Button size="sm" variant="outline" onClick={() => updateStatusMutation.mutate({ unitId: unit.id, status: 'dirty' })}>
                        Mark Dirty
                      </Button>
                    )}
                    {unit.status !== 'maintenance' && (
                      <Button size="sm" variant="outline" onClick={() => updateStatusMutation.mutate({ unitId: unit.id, status: 'maintenance' })}>
                        Maintenance
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredUnits?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <BedDouble className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No housing units found</p>
          </CardContent>
        </Card>
      )}

      {/* Add Unit Dialog */}
      <Dialog open={showAddUnit} onOpenChange={setShowAddUnit}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Housing Unit</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Unit Details</TabsTrigger>
              <TabsTrigger value="location">Location</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="space-y-4 pt-4">
              <div>
                <Label>Unit Name/Number</Label>
                <Input
                  value={newUnit.name}
                  onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
                  placeholder="Room 101"
                />
              </div>
              <div>
                <Label>Building</Label>
                <Input
                  value={newUnit.building}
                  onChange={(e) => setNewUnit({ ...newUnit, building: e.target.value })}
                  placeholder="Main Hotel"
                />
              </div>
              <div>
                <Label>Capacity</Label>
                <Input
                  type="number"
                  min="1"
                  value={newUnit.capacity}
                  onChange={(e) => setNewUnit({ ...newUnit, capacity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={newUnit.notes}
                  onChange={(e) => setNewUnit({ ...newUnit, notes: e.target.value })}
                  placeholder="Any special notes..."
                />
              </div>
            </TabsContent>
            <TabsContent value="location" className="space-y-4 pt-4">
              <div className="text-sm text-slate-500 mb-2">
                <MapPin className="h-4 w-4 inline mr-1" />
                Set the location of this housing unit on the map
              </div>
              <GoogleMapPicker
                latitude={newUnit.latitude}
                longitude={newUnit.longitude}
                address={newUnit.address}
                onLocationChange={(location) => {
                  setNewUnit(prev => ({
                    ...prev,
                    latitude: location.lat,
                    longitude: location.lng,
                    address: location.address || prev.address
                  }));
                }}
                height="300px"
                placeholder="Search for housing location..."
              />
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUnit(false)}>Cancel</Button>
            <Button onClick={() => createUnitMutation.mutate(newUnit)} disabled={!newUnit.name}>
              Add Unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HousingManager;
